import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { User, CalendarDays, Award, ChevronRight, BookOpen } from 'lucide-react';
import api from '../../utils/api';

export default function CataloguePage() {
  const { showId: paramShowId } = useParams();
  const navigate = useNavigate();
  const [shows, setShows] = useState([]);
  const [showsLoading, setShowsLoading] = useState(true);
  const [showId, setShowId] = useState(paramShowId || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: '', sex: '', breed: '', classId: '' });
  const [critiqueEntry, setCritiqueEntry] = useState(null); // entry shown in the critique modal
  const [cvOpen, setCvOpen] = useState(false); // judge's CV modal

  // Keep local selection in sync with the URL param (e.g. arriving via /catalogue/:id).
  useEffect(() => { setShowId(paramShowId || ''); }, [paramShowId]);

  useEffect(() => {
    api.get('/shows')
      .then((res) => setShows(res.data.shows))
      .finally(() => setShowsLoading(false));
  }, []);

  const load = () => {
    if (!showId) return;
    setLoading(true);
    api.get(`/shows/${showId}/catalogue`, {
      params: {
        search: filters.search || undefined,
        sex: filters.sex || undefined,
        breed: filters.breed || undefined,
        classId: filters.classId || undefined,
      },
    })
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
  };

  // Load whenever a show is selected (and reset filters/data when leaving).
  useEffect(() => {
    if (showId) {
      load();
    } else {
      setData(null);
      setFilters({ search: '', sex: '', breed: '', classId: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showId]);

  const selectShow = (id) => navigate(`/catalogue/${id}`);
  const backToShows = () => navigate('/catalogue');

  // Group entries Sex -> Class (matches the printed catalogue: "Males: Junior").
  const grouped = useMemo(() => {
    if (!data) return [];
    const sexOrder = { DOG: 0, BITCH: 1 };
    const bySex = {};
    for (const e of data.entries) {
      const sex = e.sex || 'Other';
      bySex[sex] = bySex[sex] || {};
      const className = e.showClass ? e.showClass.name : 'Unassigned';
      const classKey = `${e.showClass ? e.showClass.sortOrder : 999}|${className}`;
      bySex[sex][classKey] = bySex[sex][classKey] || { className, entries: [] };
      bySex[sex][classKey].entries.push(e);
    }
    return Object.entries(bySex)
      .sort((a, b) => (sexOrder[a[0]] ?? 9) - (sexOrder[b[0]] ?? 9))
      .map(([sex, classes]) => ({
        sex,
        label: sex === 'DOG' ? 'Males' : sex === 'BITCH' ? 'Females' : sex,
        classes: Object.entries(classes)
          .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
          .map(([, v]) => v),
      }));
  }, [data]);

  const classes = data?.show?.classes || [];

  const exportPdf = () => {
    const showName = data?.show?.name || 'catalogue';
    const prevTitle = document.title;
    // The saved PDF is named after the document title in most browsers.
    document.title = `${showName} - Catalogue`;
    window.print();
    // Restore the title shortly after the print dialog opens.
    setTimeout(() => { document.title = prevTitle; }, 500);
  };

  // -----------------------------------------------------------------------
  // Step 1: no show selected -> show a list of shows to pick from.
  // -----------------------------------------------------------------------
  if (!showId) {
    return (
      <div className="catalogue-page">
        <div className="page-head">
          <p className="eyebrow">Digital catalogue</p>
          <h1>Choose a show</h1>
          <p>Select a show below to browse its catalogue, search entries and follow the results.</p>
        </div>

        {showsLoading ? (
          <div className="loading">Loading shows…</div>
        ) : shows.length === 0 ? (
          <p className="muted">No published shows yet.</p>
        ) : (
          <div className="grid cols-2">
            {shows.map((s) => (
              <div className="card" key={s.id}>
                <h3 style={{ marginTop: 0 }}>{s.name}</h3>
                <p className="muted">
                  {format(new Date(s.showDate), 'PPP')}{s.location ? ` · ${s.location}` : ''}
                </p>
                {s.description && <p>{s.description}</p>}
                <button className="btn accent" onClick={() => selectShow(s.id)}>View catalogue</button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Step 2: a show is selected -> catalogue + search scoped to this show.
  // -----------------------------------------------------------------------
  const selectedShow = data?.show || shows.find((s) => String(s.id) === String(showId));

  return (
    <div className="catalogue-page">
      {/* Editorial hero band */}
      <section className="cat-hero no-print">
        <button className="btn ghost sm cat-hero-back" onClick={backToShows}>← All shows</button>
        <p className="cat-hero-eyebrow">Cape Rottweiler Club</p>
        <h1 className="cat-hero-title">Catalogue</h1>
        <p className="cat-hero-sub">
          <span>Show Entries</span>
          <span className="cat-hero-dot" />
          <span>Classes</span>
          <span className="cat-hero-dot" />
          <span>Results</span>
        </p>
        {selectedShow && (
          <p className="cat-hero-show">
            {selectedShow.name}
            {selectedShow.showDate ? ` · ${format(new Date(selectedShow.showDate), 'PPP')}` : ''}
            {selectedShow.location ? ` · ${selectedShow.location}` : ''}
          </p>
        )}
        {selectedShow?.judgeCv && selectedShow.judgeCv.trim() && (
          <button className="btn ghost sm cat-hero-cv" onClick={() => setCvOpen(true)}>
            <BookOpen size={14} /> View judge's CV
          </button>
        )}
      </section>

      {/* Search / filters scoped to the selected show */}
      <div className="card no-print">
        <div className="flex between" style={{ marginBottom: 'var(--sp-4)' }}>
          <h3 style={{ margin: 0 }}>Search this catalogue</h3>
          {data && data.entries.length > 0 && (
            <button className="btn ghost sm" onClick={exportPdf}>Export to PDF</button>
          )}
        </div>
        <div className="grid cols-2">
          <div className="form-row">
            <label>Search (dog, reg no, exhibitor)</label>
            <input
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
              placeholder="Start typing then Search"
            />
          </div>
          <div className="form-row">
            <label>Class</label>
            <select value={filters.classId} onChange={(e) => setFilters({ ...filters, classId: e.target.value })}>
              <option value="">All classes</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.sex})</option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>Sex</label>
            <select value={filters.sex} onChange={(e) => setFilters({ ...filters, sex: e.target.value })}>
              <option value="">All</option>
              <option value="DOG">Dogs</option>
              <option value="BITCH">Bitches</option>
            </select>
          </div>
          <div className="form-row">
            <label>Breed</label>
            <input value={filters.breed} onChange={(e) => setFilters({ ...filters, breed: e.target.value })} />
          </div>
        </div>
        <div className="flex">
          <button className="btn" onClick={load}>Search</button>
          {(filters.search || filters.sex || filters.breed || filters.classId) && (
            <button className="btn ghost" onClick={() => { setFilters({ search: '', sex: '', breed: '', classId: '' }); setTimeout(load, 0); }}>
              Clear
            </button>
          )}
        </div>
      </div>

      {loading && <div className="loading no-print">Loading catalogue…</div>}

      {!loading && data && data.catalogueReleased === false && (
        <div className="card no-print" style={{ textAlign: 'center' }}>
          <h3 style={{ marginTop: 0 }}>Catalogue not yet available</h3>
          <p className="muted">
            The catalogue for this show has not been released yet. It will appear here once entries
            have been finalised. Please check back closer to the show.
          </p>
        </div>
      )}

      {!loading && data && data.catalogueReleased !== false && data.entries.length === 0 && (
        <p className="muted no-print">No entries match your search in this catalogue.</p>
      )}

      {/* Print-only header for the exported PDF */}
      {!loading && data && data.entries.length > 0 && (
        <div className="print-only print-header">
          <h1>{data.show?.name}</h1>
          {data.show?.showDate && (
            <p>{format(new Date(data.show.showDate), 'PPP')}{data.show?.location ? ` · ${data.show.location}` : ''}</p>
          )}
          <p>Show Catalogue</p>
        </div>
      )}

      {/* Print-only judge's CV intro at the top of the exported PDF */}
      {!loading && data && data.entries.length > 0 && data.show?.judgeCv && data.show.judgeCv.trim() && (
        <div className="print-only print-cv">
          <h2>{data.show?.judgeName ? `Our judge: ${data.show.judgeName}` : 'Our judge'}</h2>
          <p style={{ whiteSpace: 'pre-wrap' }}>{data.show.judgeCv}</p>
        </div>
      )}

      {!loading && grouped.map((sexGroup) => (
        <div key={sexGroup.sex}>
          {sexGroup.classes.map((cls) => (
            <section key={cls.className} className="cat-class">
              <h2 className="cat-class-heading">{sexGroup.label}: {cls.className}</h2>
              <div className="cat-list">
                {cls.entries.map((e) => (
                  <CatalogueCard key={e.id} entry={e} onShowCritique={setCritiqueEntry} />
                ))}
              </div>
            </section>
          ))}
        </div>
      ))}

      {critiqueEntry && (
        <CritiqueModal entry={critiqueEntry} onClose={() => setCritiqueEntry(null)} />
      )}

      {cvOpen && (
        <JudgeCvModal show={selectedShow} onClose={() => setCvOpen(false)} />
      )}
    </div>
  );
}

function JudgeCvModal({ show, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="flex between">
          <h3 style={{ margin: 0 }}>{show?.judgeName ? `Our judge: ${show.judgeName}` : "Judge's CV"}</h3>
          <button className="btn ghost sm" onClick={onClose}>✕</button>
        </div>
        <div className="muted" style={{ marginBottom: '0.75rem' }}>Judge's curriculum vitae</div>
        <p style={{ whiteSpace: 'pre-wrap' }}>{show?.judgeCv}</p>
      </div>
    </div>
  );
}

function CritiqueModal({ entry, onClose }) {
  const c = entry.critique;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="flex between">
          <h3 style={{ margin: 0 }}>
            {entry.catalogueCode ? `${entry.catalogueCode} · ` : ''}{entry.dogName}
          </h3>
          <button className="btn ghost sm" onClick={onClose}>✕</button>
        </div>
        <div className="muted" style={{ marginBottom: '0.75rem' }}>
          Judge's Critique
        </div>
        {c ? (
          <>
            <p style={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>{c.text}</p>
            <div className="muted">
              — {c.judgeName}
              {c.critiqueDate ? ` · ${format(new Date(c.critiqueDate), 'PP')}` : ''}
            </div>
          </>
        ) : (
          <p className="muted">No critique has been published for this dog yet.</p>
        )}
      </div>
    </div>
  );
}

function CatalogueCard({ entry, onShowCritique }) {
  const sexLabel = entry.sex === 'DOG' ? 'Dog' : entry.sex === 'BITCH' ? 'Bitch' : entry.sex;
  const dob = entry.dateOfBirth ? format(new Date(entry.dateOfBirth), 'yyyy/MM/dd') : '—';
  const code = entry.catalogueCode || (entry.catalogueNumber != null ? `#${entry.catalogueNumber}` : '—');
  const className = entry.showClass ? entry.showClass.name : '';
  const gradeText = entry.grade ? `${entry.grade.name}${entry.placing != null ? entry.placing : ''}` : null;

  return (
    <article className="cat-row">
      {/* Left: catalogue code + class name */}
      <div className="cat-row-code">
        <div className="cat-row-no">{code}</div>
        {className && <div className="cat-row-class">{className}</div>}
      </div>

      {/* Middle: dog identity + pedigree */}
      <div className="cat-row-main">
        <div className="cat-row-name">
          {entry.qualifications ? `${entry.qualifications} ` : ''}{entry.dogName}
        </div>
        <div className="cat-row-breed">{entry.breed || sexLabel}</div>

        <dl className="cat-row-facts">
          <div><dt>Reg. No</dt><dd>{entry.registrationNumber || '—'}</dd></div>
          <div><dt>Chip. No</dt><dd>{entry.microchip || '—'}</dd></div>
          <div><dt>Sire</dt><dd className="italic">{entry.sireName || '—'}</dd></div>
          <div><dt>Dam</dt><dd className="italic">{entry.damName || '—'}</dd></div>
          {entry.tattoo && <div><dt>Tattoo</dt><dd>{entry.tattoo}</dd></div>}
        </dl>

        {entry.critique && (
          <div className="no-print" style={{ marginTop: 'var(--sp-3)' }}>
            <button className="btn ghost sm" onClick={() => onShowCritique(entry)}>
              Show judge's critique
            </button>
          </div>
        )}
        {/* Printed PDF shows the critique inline rather than a button. */}
        {entry.critique && (
          <div className="print-only" style={{ marginTop: '0.5rem' }}>
            <strong>Judge's critique:</strong> <em>{entry.critique.text}</em>
            <div className="muted">— {entry.critique.judgeName}</div>
          </div>
        )}
      </div>

      {/* Right: owner / breeder metadata + grade pill */}
      <div className="cat-row-meta">
        {gradeText && (
          <span className="cat-grade-pill"><Award size={13} /> {gradeText}</span>
        )}
        <div className="cat-meta-item">
          <span className="cat-meta-icon"><User size={14} /></span>
          <span><span className="cat-meta-label">Owner</span>{entry.ownerName || '—'}</span>
        </div>
        <div className="cat-meta-item">
          <span className="cat-meta-icon"><CalendarDays size={14} /></span>
          <span><span className="cat-meta-label">DOB</span>{dob}</span>
        </div>
        <div className="cat-meta-item">
          <span className="cat-meta-icon"><Award size={14} /></span>
          <span><span className="cat-meta-label">Breeder</span>{entry.breederName || '—'}</span>
        </div>
      </div>

      <ChevronRight className="cat-row-chevron" size={20} />
    </article>
  );
}
